import type { ReactNode } from "react"
import { Landmark } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useAuth } from "@/lib/auth"
import { t } from "@/lib/i18n"

/**
 * Puerta de las pantallas que no significan nada sin una sociedad.
 *
 * DECISION (y por que): cuando no hay entidad activa, la pantalla lo DICE y no
 * llama a la API. La alternativa —mandar la peticion sin `entity_id` y traducir
 * el 422 que devuelve el backend— era lo que pasaba antes con `VITE_ENTITY_ID`
 * vacio, y es peor por tres razones:
 *
 *  1. el mensaje del 422 nombra un campo del backend (`entity_id`) que el
 *     usuario no puede rellenar desde ninguna parte de la pantalla;
 *  2. llega DESPUES de que la persona haya tecleado todo el formulario, que es
 *     el peor momento posible para enterarse;
 *  3. en los LISTADOS ni siquiera falla: `entity_id` es opcional en los GET, asi
 *     que sin sociedad activa se veria la mezcla de todas las sociedades del
 *     cliente como si fuera una sola. Eso no es un error visible — es un numero
 *     equivocado con aspecto de correcto, que es exactamente lo que un producto
 *     contable no se puede permitir.
 *
 * Falta elegir sociedad, entonces, no es un estado de error: es un paso que la
 * pantalla pide con nombre y apellido y con el foco puesto en el selector de la
 * cabecera, que es donde se resuelve.
 */
export function RequireEntity({
  children,
  que = "esta pantalla",
}: {
  children: ReactNode
  /** Que es lo que no se puede ver: "sus contratos", "su gente"… */
  que?: string
}) {
  const { activeEntityId, entities, entitiesLoading } = useAuth()

  if (activeEntityId) return <>{children}</>

  // Mientras se averigua que sociedades hay no se afirma nada: si el cliente
  // tiene una sola, se va a elegir sola en un instante y este cartel seria un
  // parpadeo pidiendo algo que nadie tiene que hacer.
  if (entitiesLoading) return null

  const sinSociedades = entities.length === 0

  return (
    <div className="rounded-panel border border-dashed border-border-strong bg-surface p-8">
      <div className="mx-auto flex max-w-[52ch] flex-col items-center gap-2 text-center">
        <div className="grid h-9 w-9 place-items-center rounded-full border border-dashed border-warning/60 bg-warning-soft text-warning">
          <Landmark className="h-4 w-4" />
        </div>
        <p className="text-sm font-medium text-foreground">
          {sinSociedades
            ? "Este cliente todavia no tiene ninguna sociedad en tu alcance."
            : `Elige una sociedad para ver ${que}.`}
        </p>
        <p className="text-xs leading-[1.15rem] text-muted-foreground">
          {sinSociedades
            ? "Los contratos, las personas y los centros de costo cuelgan de una sociedad. Hasta que exista una —o hasta que te den alcance sobre ella— no hay nada que configurar aca."
            : "Un contrato, una persona o un centro de costo pertenecen a UNA sociedad, no al cliente entero. Mostrarlos todos juntos daria un total que no es de nadie."}
        </p>
        {sinSociedades ? null : (
          <Button
            size="xs"
            variant="outline"
            className="mt-1"
            onClick={() => {
              // El selector vive en la cabecera y este boton lleva hasta el.
              // Radix abre el desplegable con `pointerdown`, no con `click`:
              // un `.click()` a secas dejaria el foco puesto y la lista cerrada,
              // que es un boton que aparenta no hacer nada.
              const selector = document.querySelector<HTMLElement>('[aria-label="Sociedad activa"]')
              selector?.focus()
              selector?.dispatchEvent(
                new PointerEvent("pointerdown", { bubbles: true, button: 0, pointerType: "mouse" }),
              )
            }}
          >
            <Landmark />
            {t("Elegir sociedad")}</Button>
        )}
      </div>
    </div>
  )
}
